// hooks/useServerErrorNotifier.ts
import { useCallback } from "react";

import { useNotification } from "../components/NotificationProvider";

export type ServerErrorDetail = {
	errorMessage: string;
};

export type ServerError = {
	message: string;
	errors?: ServerErrorDetail[];
};

export const useNotifier: () => {
	notifySuccess: (message: string) => void;
	notifyFailure: (message: string) => void;
	notifyErrors: (errors?: ServerError[]) => void;
	notifyException: (error: unknown) => void;
} = () => {
	const { showNotification } = useNotification();

	const notifySuccess = useCallback(
		(message: string): void => {
			showNotification(message, "success");
		},
		[showNotification],
	);

	const notifyFailure = useCallback(
		(message: string): void => {
			showNotification(message, "error");
		},
		[showNotification],
	);

	const notifyErrors = useCallback(
		(errors?: ServerError[]): void => {
			if (!errors || errors.length === 0) {
				return;
			}

			errors.forEach((error) => {
				if (!error.errors || error.errors.length === 0) {
					showNotification(error.message, "error");
					return;
				}

				error.errors.forEach((detail) => {
					showNotification(detail.errorMessage, "error");
				});
			});
		},
		[showNotification],
	);

	const notifyException = useCallback(
		(error: unknown): void => {
			const message = error instanceof Error ? error.message : "An unexpected error occurred.";
			showNotification(message, "error");
		},
		[showNotification],
	);

	return { notifySuccess, notifyFailure, notifyErrors, notifyException };
};
